/**
 * TankBatteryDetail — Phase 24 / Task 24.3 (DEC-094).
 *
 * Detail panel for one tank battery: linked WELLS (link/unlink), TANKS
 * (add/edit/deactivate), and the AUDIT TRAIL (plain-English, no UUIDs — visible
 * to all dispatcher roles). Writes are gated on canManage (server is authoritative).
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Pencil } from 'lucide-react';
import {
  useTankBattery,
  useTankBatteryAudit,
  TANK_BATTERY_AUDIT_KEY,
  TANK_BATTERY_KEY,
  useStandardTanks,
  useUnlinkWell,
  useDeactivateTank,
  useRenameTankBattery,
  roleLabel,
  type CatalogTank,
  type LinkedWell,
  type TankBatterySummary,
} from './hooks';
import { LinkWellModal } from './LinkWellModal';
import { AddTankModal } from './AddTankModal';
import { SiteCountyField } from './SiteCountyField';
import { RequestorSlots } from '../requestors/RequestorSlots';
import { PlaceNotesEditor } from '../place-notes/PlaceNotesEditor';
import { ActiveBadge, AddButton, Pill, rowStyle, subText } from './ui-bits';
import { AutoHaulSwitch } from './AutoHaulSwitch';
import { suggestBblPerInch } from '@lodex-pro/shared/tank-strapping';
import { TankSizeHint } from './TankSizeHint';
import { formatNumber } from '@lodex-pro/shared/number-format';

function fmtAuditTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Task 24.6 — geofence status pill that links to the geofence editor. */
function GeofenceBadge({
  exists,
  verified,
  onOpen,
}: {
  exists: boolean;
  verified: boolean;
  onOpen: () => void;
}): React.JSX.Element {
  const label = verified ? 'Geofence verified' : exists ? 'Geofence draft' : 'Geofence unverified';
  const color = verified
    ? 'var(--color-success)'
    : exists
      ? 'var(--color-warning)'
      : 'var(--color-text-tertiary)';
  return (
    <button
      type="button"
      onClick={onOpen}
      title="Open the geofence editor"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '11px',
        fontWeight: 600,
        color,
        background: 'none',
        border: `1px solid ${color}`,
        borderRadius: '12px',
        padding: '2px 8px',
        cursor: 'pointer',
      }}
    >
      <MapPin size={11} aria-hidden="true" /> {label}
    </button>
  );
}

function WellRow({
  batteryId,
  well,
  canManage,
}: {
  batteryId: string;
  well: LinkedWell;
  canManage: boolean;
}): React.JSX.Element {
  const unlink = useUnlinkWell(batteryId, well.wellId);
  return (
    <div style={rowStyle}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 600 }}>{well.wellName}</span>
        <div style={subText}>
          API {well.apiNumber}
          {well.county != null ? ` · ${well.county}` : ''} · {well.state}
          {well.wellType != null ? ` · ${well.wellType}` : ''}
        </div>
      </div>
      {canManage && (
        <button
          type="button"
          disabled={unlink.isPending}
          onClick={(): void =>
            unlink.mutate(undefined, {
              onSuccess: () => toast.success('Well unlinked'),
              onError: () => toast.error('Could not unlink well'),
            })
          }
          style={{
            fontSize: '12px',
            color: 'var(--color-danger)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Unlink
        </button>
      )}
    </div>
  );
}

function TankRow({
  batteryId,
  tank,
  canManage,
  onEdit,
}: {
  batteryId: string;
  tank: CatalogTank;
  canManage: boolean;
  onEdit: (t: CatalogTank) => void;
}): React.JSX.Element {
  const deactivate = useDeactivateTank(tank.id, batteryId);
  const { data: std } = useStandardTanks();
  return (
    <div style={{ ...rowStyle, opacity: tank.isActive ? 1 : 0.55 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontWeight: 600 }}>{tank.name}</span>
          {!tank.isActive && <Pill label="Inactive" tone="muted" />}
        </div>
        <div style={subText}>
          {tank.capacityBbl != null ? `${formatNumber(tank.capacityBbl)} bbl` : 'No capacity'}
          {tank.bblPerInch != null ? ` · ${tank.bblPerInch} bbl/in` : ''}
          {tank.tankType != null ? ` · ${tank.tankType}` : ''}
          {tank.producerIdentifier != null ? ` · "${tank.producerIdentifier}"` : ''}
        </div>
        {/* DEC-377 — no bbl per inch: the standard size its estimate uses, or the options. */}
        {tank.isActive && (
          <TankSizeHint
            tankId={tank.id}
            bblPerInch={tank.bblPerInch}
            suggestion={suggestBblPerInch(tank, std?.standardTanks ?? [])}
            canManage={canManage}
            whereToSet={canManage ? 'with Edit' : 'on this tank (a Dispatch Manager can)'}
            refresh={[TANK_BATTERY_KEY(batteryId), TANK_BATTERY_AUDIT_KEY(batteryId)]}
          />
        )}
      </div>
      {canManage && tank.isActive && (
        <>
          <button
            type="button"
            onClick={(): void => onEdit(tank)}
            style={{
              fontSize: '12px',
              color: 'var(--color-accent-blue)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Edit
          </button>
          <button
            type="button"
            disabled={deactivate.isPending}
            onClick={(): void =>
              deactivate.mutate(undefined, {
                onSuccess: () => toast.success('Tank deactivated'),
                onError: () => toast.error('Could not deactivate tank'),
              })
            }
            style={{
              fontSize: '12px',
              color: 'var(--color-danger)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Deactivate
          </button>
        </>
      )}
    </div>
  );
}

const sectionTitle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 700,
  color: 'var(--color-text-tertiary)',
  margin: '18px 0 8px',
};

export interface TankBatteryDetailProps {
  readonly battery: TankBatterySummary;
  readonly operatorPlatformId: string | null;
  readonly canManage: boolean;
  readonly onClose: () => void;
}

export function TankBatteryDetail({
  battery,
  operatorPlatformId,
  canManage,
  onClose,
}: TankBatteryDetailProps): React.JSX.Element {
  const { data, isLoading } = useTankBattery(battery.id);
  const { data: auditData } = useTankBatteryAudit(battery.id);
  const detail = data?.tankBattery;
  const audit = auditData?.audit ?? [];

  const navigate = useNavigate();
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [tankOpen, setTankOpen] = React.useState(false);
  const [editingTank, setEditingTank] = React.useState<CatalogTank | null>(null);
  // Task 24.6 — inline rename (DISPATCH_MANAGER+).
  const [renaming, setRenaming] = React.useState(false);
  const [nameInput, setNameInput] = React.useState(battery.name);
  const rename = useRenameTankBattery(battery.id);

  const canLinkWells = canManage && operatorPlatformId !== null && !battery.isPrivate;

  return (
    <div>
      <button
        type="button"
        onClick={onClose}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '13px',
          color: 'var(--color-text-secondary)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          marginBottom: '12px',
        }}
      >
        <ArrowLeft size={14} aria-hidden="true" /> Back to tank batteries
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        {renaming ? (
          <>
            <input
              value={nameInput}
              onChange={(e): void => setNameInput(e.target.value)}
              style={{
                fontSize: '16px',
                padding: '4px 8px',
                backgroundColor: 'var(--color-bg-tertiary)',
                border: '1px solid var(--color-border-secondary)',
                borderRadius: '10px',
                color: 'var(--color-text-primary)',
              }}
            />
            <button
              type="button"
              disabled={rename.isPending || nameInput.trim() === ''}
              onClick={(): void =>
                rename.mutate(
                  { name: nameInput.trim() },
                  {
                    onSuccess: () => {
                      toast.success('Renamed');
                      setRenaming(false);
                    },
                    onError: () => toast.error('Could not rename'),
                  },
                )
              }
              style={{
                fontSize: '13px',
                color: 'var(--color-accent-blue)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Save
            </button>
            <button
              type="button"
              onClick={(): void => {
                setNameInput(battery.name);
                setRenaming(false);
              }}
              style={{
                fontSize: '13px',
                color: 'var(--color-text-secondary)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <h2 style={{ fontSize: '18px', fontWeight: 700 }}>{battery.name}</h2>
            {canManage && (
              <button
                type="button"
                aria-label="Rename tank battery"
                onClick={(): void => {
                  setNameInput(battery.name);
                  setRenaming(true);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-text-secondary)',
                }}
              >
                <Pencil size={14} aria-hidden="true" />
              </button>
            )}
          </>
        )}
        <ActiveBadge active={battery.isActive} />
        {battery.isPrivate && <Pill label="Private" tone="muted" />}
        <GeofenceBadge
          exists={detail?.geofenceExists === true}
          verified={detail?.geofenceVerified === true}
          onOpen={(): void => {
            void navigate('/geofences');
          }}
        />
      </div>
      <p style={subText}>
        {battery.operatorName} ({battery.operatorAbbreviation})
      </p>
      {/* COUNTY (DEC-310) — prefer the detail values: they are fresh after the PATCH invalidates. */}
      <SiteCountyField
        siteId={battery.id}
        county={detail?.county ?? battery.county}
        countyState={detail?.countyState ?? battery.countyState}
        countySource={detail?.countySource ?? battery.countySource}
        canManage={canManage}
      />

      {/* 13.4a (0236) — auto-haul is switched on per site, off by default. */}
      <AutoHaulSwitch
        enabled={detail?.autoHaulEnabled}
        canManage={canManage && battery.isActive}
        pending={rename.isPending}
        idSuffix={battery.id}
        onChange={(next, done): void => rename.mutate({ autoHaulEnabled: next }, done)}
      />

      {/* REQUESTORS (OPEN-053) — site metadata, so it sits with the header rather than
          below the wells/tanks lists a dispatcher may have to scroll past. */}
      <RequestorSlots targetType="TANK_BATTERY" targetId={battery.id} canManage={canManage} />

      {/* WELLS */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={sectionTitle}>Wells {detail ? `(${detail.wells.length})` : ''}</div>
        {canLinkWells && <AddButton label="Link Well" onClick={(): void => setLinkOpen(true)} />}
      </div>
      {battery.isPrivate && (
        <p style={subText}>Private site — wells are not linked from the platform catalog.</p>
      )}
      {isLoading && <p style={subText}>Loading…</p>}
      {detail?.wells.length === 0 && !battery.isPrivate && <p style={subText}>No wells linked.</p>}
      {detail?.wells.map((w) => (
        <WellRow key={w.linkId} batteryId={battery.id} well={w} canManage={canManage} />
      ))}

      {/* TANKS */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={sectionTitle}>Tanks {detail ? `(${detail.tanks.length})` : ''}</div>
        {canManage && (
          <AddButton
            label="Add Tank"
            onClick={(): void => {
              setEditingTank(null);
              setTankOpen(true);
            }}
          />
        )}
      </div>
      {detail?.tanks.length === 0 && <p style={subText}>No tanks yet.</p>}
      {detail?.tanks.map((t) => (
        <TankRow
          key={t.id}
          batteryId={battery.id}
          tank={t}
          canManage={canManage}
          onEdit={(tank): void => {
            setEditingTank(tank);
            setTankOpen(true);
          }}
        />
      ))}

      {/* NOTES FOR DRIVERS (#45 / DEC-359) — the change is recorded in the audit trail below. */}
      <div style={sectionTitle}>Notes for drivers</div>
      <PlaceNotesEditor
        kind="LOCATION"
        id={battery.id}
        placeName={battery.name}
        canManage={canManage && battery.isActive}
        alsoInvalidate={[TANK_BATTERY_AUDIT_KEY(battery.id)]}
      />

      {/* AUDIT TRAIL */}
      <div style={sectionTitle}>Audit trail</div>
      {audit.length === 0 && <p style={subText}>No changes recorded.</p>}
      {audit.map((a) => (
        <div key={a.id} style={{ ...rowStyle, display: 'block' }}>
          <div style={subText}>{fmtAuditTime(a.changedAt)}</div>
          <div style={{ fontSize: '13px' }}>
            {a.changedByName ?? 'System'}
            {a.changedByRole != null ? ` (${roleLabel(a.changedByRole)})` : ''}
          </div>
          <div style={{ fontSize: '14px' }}>{a.notes ?? a.action}</div>
        </div>
      ))}

      {canLinkWells && operatorPlatformId !== null && (
        <LinkWellModal
          open={linkOpen}
          onOpenChange={setLinkOpen}
          batteryId={battery.id}
          batteryName={battery.name}
          operatorId={operatorPlatformId}
        />
      )}
      <AddTankModal
        open={tankOpen}
        onOpenChange={setTankOpen}
        batteryId={battery.id}
        editing={editingTank}
      />
    </div>
  );
}
